import { FC, useState, useEffect, useRef } from 'react'
import clsx from 'clsx'

import Auth from 'store/auth'
import CommentItem from './comment-item/comment-item'
import CommentsBottom from './comments-bottom/comments-bottom'
import WidthLimiter from 'containers/width-limiter/width-limiter'

import { postAxiosSingle, getAxiosSingle, deleteAxiosSingle } from 'pages/api/axios'
import { formatComments } from './utils'
import { urlify } from 'utils/urlify'
import { ICommentDataset } from 'constants/types/webinar.types'
import { CommentURL, CommentURLNames } from './comments'

import styles from './commentaries.module.scss'

interface ICommentaries {
  comments?: ICommentDataset[]
  id?: number
  materialType: string
  isWithNMO?: boolean
  getCommentsAmount?: (amount: number) => void
  widthLimited?: boolean
  isAllowComment?: boolean
}

const Commentaries: FC<ICommentaries> = ({
  comments,
  id,
  materialType,
  isWithNMO = false,
  getCommentsAmount,
  widthLimited,
  isAllowComment = true,
}: ICommentaries) => {
  const { isAdmin, userProfile } = Auth.useAuth()
  const [inputText, setInputText] = useState('')
  const [commentsData, setCommentsData] = useState({
    items: comments,
    amount: 0,
    isShowBtnMore: false,
  })
  const [isSendBlocked, setSendBlock] = useState(false)
  const isShowAllComments = useRef(true)

  const showAllComments = () => {
    isShowAllComments.current = false
    setCommentsData((prevState) => ({ ...prevState, isShowBtnMore: false }))
  }

  const formatText = (text?: string) => {
    if (!text) return ''
    if (isAdmin || userProfile?.tags?.includes('lecturer')) return urlify(text)
    return text
  }

  const receptionComments = async (data: any) => {
    setSendBlock(true)
    if (data.text && !data.answerTo) isShowAllComments.current = false
    const res = await postAxiosSingle(CommentURL.COMMENT, {}, data)
    if (res.status === 200) {
      getComments()
      setSendBlock(false)
    }
    setInputText('')
    // showAllComments()
  }

  const handleSend = async () => {
    if (!inputText) return
    const dataToSend = { text: formatText(inputText), materialId: id, materialType }
    receptionComments(dataToSend)
  }

  const handleSendAnswer = async (
    idAnswer?: number | string,
    textAnswer?: string,
    idComment?: number | string
  ) => {
    if (!textAnswer) return
    const idNeedful = idComment ? idComment : idAnswer
    const dataToSend = {
      text: formatText(textAnswer),
      materialId: id,
      materialType,
      answerTo: idNeedful,
    }
    receptionComments(dataToSend)
  }

  const formatData = (data?: ICommentDataset[]) => {
    if (data) {
      const [firstStage, length] = formatComments(data)
      getCommentsAmount && getCommentsAmount(length)
      setCommentsData({
        items: firstStage,
        amount: length,
        isShowBtnMore: Boolean(firstStage.length > 3 && isShowAllComments.current),
      })
    }
  }

  const getComments = async () => {
    const articleData = await getAxiosSingle(`${CommentURL[materialType]}/${id}`)
    formatData(articleData.Comments)
  }

  const deleteComment = async (commentID: number | string) => {
    const res = await deleteAxiosSingle(`${CommentURL.COMMENT}/${commentID}`)
    if (res.status === 200) {
      getComments()
    }
  }

  useEffect(() => {
    formatData(comments)
  }, [comments])

  return (
    <div className={styles.comment}>
      <WidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
        <h3 className={clsx(styles.title)}>
          <span>{materialType === CommentURLNames.CLINICAL_CASE ? 'Ответы' : 'Комментарии'}</span>
          <span>&nbsp;•&nbsp;</span>
          <span>{commentsData.amount}</span>
        </h3>
      </WidthLimiter>

      <div
        className={clsx(styles.commentContainer, { [styles.containerWidthLimited]: widthLimited })}
      >
        <WidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
          {commentsData.items && commentsData.items.length > 0 && (
            <div className={styles.commentBox}>
              {commentsData.items
                .slice(0, commentsData.isShowBtnMore ? 3 : undefined)
                .map((comment, index) => {
                  const isLastComment =
                    commentsData.items && index === commentsData.items.length - 1
                  const isThirdComment = index == 2

                  const messageItem = {
                    deleteComment,
                    handleSendAnswer,
                    isAllowComment,
                    modificator:
                      (commentsData.isShowBtnMore && isThirdComment) || isLastComment
                        ? ''
                        : styles.commentUser,
                    ...comment,
                  }
                  return (
                    <div key={comment._id} className={styles.commentWrapper}>
                      {comment.answers ? (
                        <div data-gg={index}>
                          <CommentItem {...messageItem} />
                          {comment.answers.map((answer) => (
                            <div key={answer._id}>
                              <CommentItem
                                deleteComment={deleteComment}
                                handleSendAnswer={handleSendAnswer}
                                modificator={styles.answer}
                                {...answer}
                                parentCommentId={comment._id}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div>
                          <CommentItem {...messageItem} />
                        </div>
                      )}
                    </div>
                  )
                })}
              {commentsData.isShowBtnMore && (
                <div className={styles.showMore} onClick={showAllComments}>
                  Посмотреть все ответы
                </div>
              )}
            </div>
          )}
          <CommentsBottom
            handleSend={handleSend}
            isSendBlocked={isSendBlocked}
            setInputText={setInputText}
            inputText={inputText}
            isWithNMO={isWithNMO}
            isAllowComment={isAllowComment}
          />
        </WidthLimiter>
      </div>
    </div>
  )
}

export default Commentaries
